import { createGlobalState } from '@vueuse/core';
import { inject, nextTick, ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { campaignsClient } from '~/services';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL } from '~/constants/campaign';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';

export const useCampaignDetails = createGlobalState(() => {
    const FEATURES = inject('FEATURES');
    const toasted = useToasted();

    const defaultCampaign = () => {
        return {
            name: '',
            type: FEATURES.transactional_default ? AUDIENCE_TYPE_TRANSACTIONAL : AUDIENCE_TYPE_MARKETING,
            duration_type: '',
            live_at: new Date(),
            original_live_at: toUTCDateTimeFormat(dayjs()),
            original_campaign_id: null,
            segments: [],
            everyone: false,
            remove_on_response: false,
            remove_on_link_click: false,
            time_zone_aware: false,
            is_repeatable: false,
            repeat_delay: 10080,
            audience_percentage: null,
            campaign_messages: [],
            category: '',
        };
    };

    const catchRequestError = (error) => {
        console.error(error);
        if (
            !error?.response?.status ||
            error?.response?.status >= HTTP_INTERNAL_SERVER_ERROR ||
            error?.response.status === HTTP_NOT_FOUND
        ) {
            toasted?.global.platform_error();
        } else {
            toasted?.error(error.response?.data?.message || 'Something went wrong.');
        }
    };

    const teamId = Spark.state.currentTeam.id;

    const campaign = ref(defaultCampaign());
    const loading = ref(true);
    const error = ref('');

    let onMountedCampaignCallback = () => {};
    const onMountedCampaign = (callback) => {
        onMountedCampaignCallback = callback;
    };
    const mount = async (campaignId) => {
        await nextTick();
        campaign.value = defaultCampaign();
        campaign.value.id = campaignId;

        if (!campaignId) {
            loading.value = false;
            onMountedCampaignCallback?.();
            return;
        }

        loading.value = true;
        try {
            const response = await campaignsClient.campaignGet(teamId, campaignId);
            campaign.value = response.data;
            loading.value = false;
            onMountedCampaignCallback?.();
        } catch (e) {
            catchRequestError(e);
        }
    };

    const unmount = () => {
        campaign.value = defaultCampaign();
    };

    return {
        campaign,
        loading,
        error,
        mount,
        unmount,
        onMountedCampaign,
    };
});
